import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Database, Loader2, Save, ShieldCheck, Trash2, Upload } from "lucide-react";
import { api, apiError, fileUrl, logError, uploadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Switch } from "@/components/ui/switch";
import { PageHeader } from "@/components/Layout";
import { Field } from "@/pages/Leads";
import { AiSettings } from "@/pages/AiSettings";
import { AuditLog } from "@/pages/AuditLog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

export default function Settings() {
  const { user } = useAuth();
  const isOwner = user?.role === "super_admin";
  const [googlePolicy, setGooglePolicy] = useState(null);
  const [settings, setSettings] = useState(null);
  const [wa, setWa] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [audit, setAudit] = useState([]);
  const [demo, setDemo] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const [s, w, t] = await Promise.all([
        api.get("/settings"),
        api.get("/whatsapp/status"),
        api.get("/whatsapp/templates"),
      ]);
      setSettings(s.data);
      setWa(w.data);
      setTemplates(t.data);
    } catch (e) {
      toast.error(apiError(e));
    }
    api.get("/audit-logs?limit=60").then(({ data }) => setAudit(data)).catch((e) => logError("audit log", e));
    api.get("/auth/google/policy").then(({ data }) => setGooglePolicy(data))
      .catch((e) => logError("google policy", e));
    api.get("/demo/status").then(({ data }) => setDemo(data)).catch((e) => logError("demo status", e));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saveSection = async (key) => {
    setBusy(key);
    try {
      const { data } = await api.put(`/settings/${key}`, settings[key]);
      setSettings(data);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const set = (section, key, value) =>
    setSettings((s) => ({ ...s, [section]: { ...s[section], [key]: value } }));

  const saveGooglePolicy = async () => {
    try {
      const { data } = await api.put("/auth/google/policy", {
        auto_create: Boolean(googlePolicy?.auto_create),
        allowed_domain: (googlePolicy?.allowed_domain || "").trim(),
      });
      setGooglePolicy(data);
      toast.success("Sign-in rules saved");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!settings)
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );

  return (
    <div>
      <PageHeader
        testId="settings-header"
        title="Settings"
        subtitle="Company profile, GST, numbering, quotation rules, WhatsApp, AI and demo data."
      />
      <div className="p-4 sm:p-6">
        <Tabs defaultValue="company">
          <TabsList data-testid="settings-tabs"
            className="max-w-full flex-nowrap overflow-x-auto sm:flex-wrap">
            <TabsTrigger value="company" className="shrink-0">Company</TabsTrigger>
            <TabsTrigger value="gst" className="shrink-0">GST</TabsTrigger>
            <TabsTrigger value="quotation" className="shrink-0">Quotation</TabsTrigger>
            <TabsTrigger value="numbering" className="shrink-0">Numbering</TabsTrigger>
            <TabsTrigger value="whatsapp" className="shrink-0">WhatsApp</TabsTrigger>
            <TabsTrigger value="ai" className="shrink-0">AI</TabsTrigger>
            {isOwner && <TabsTrigger value="signin" className="shrink-0" data-testid="settings-tab-signin">Sign-in</TabsTrigger>}
            <TabsTrigger value="demo" className="shrink-0">Demo data</TabsTrigger>
            <TabsTrigger value="audit" className="shrink-0" data-testid="settings-tab-audit">Audit log</TabsTrigger>
          </TabsList>

          <TabsContent value="company">
            <div className="surface mt-4 p-5" data-testid="settings-company">
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {[
                  ["name", "Company name"], ["legal_name", "Legal name"], ["tagline", "Tagline"],
                  ["logo_url", "Logo URL"], ["address_line1", "Address line 1"],
                  ["address_line2", "Address line 2"], ["city", "City"], ["state", "State"],
                  ["pincode", "Pincode"], ["phone", "Phone"], ["email", "Email"],
                  ["website", "Website"], ["gstin", "GSTIN"], ["pan", "PAN"], ["cin", "CIN"],
                  ["bank_name", "Bank name"], ["bank_account", "Account number"],
                  ["bank_ifsc", "IFSC"], ["bank_branch", "Branch"], ["upi_id", "UPI ID"],
                  ["signatory", "Signatory label"], ["signature_url", "Signature image URL"],
                ].map(([k, l]) => (
                  <Field key={k} label={l}>
                    <Input
                      data-testid={`company-${k}-input`}
                      value={settings.company[k] || ""}
                      onChange={(e) => set("company", k, e.target.value)}
                    />
                  </Field>
                ))}
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-4 rounded border border-dashed border-border p-4">
                <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
                  <Upload className="h-4 w-4" /> Upload digital signature (PNG with transparent background works best)
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    data-testid="signature-upload-input"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setBusy("signature");
                      try {
                        const up = await uploadFile(file, "signature");
                        const { data } = await api.put("/settings/company", {
                          ...settings.company, signature_url: up.url,
                        });
                        setSettings(data);
                        toast.success("Signature uploaded — approved documents will be signed automatically");
                      } catch (err) {
                        toast.error(apiError(err));
                      } finally {
                        setBusy("");
                      }
                    }}
                  />
                </label>
                {busy === "signature" && <Loader2 className="h-4 w-4 animate-spin" />}
                {settings.company.signature_url && (
                  <img
                    src={fileUrl(settings.company.signature_url)}
                    alt="Authorised signature"
                    data-testid="signature-preview"
                    className="h-12 rounded bg-white object-contain px-2"
                  />
                )}
              </div>
              <Button className="mt-4 gap-1.5" data-testid="save-company-button" disabled={busy === "company"} onClick={() => saveSection("company")}>
                <Save className="h-4 w-4" /> Save company profile
              </Button>
              <p className="mt-2 text-xs text-muted-foreground">
                These details appear on every quotation, invoice and PDF. Nothing is invented — blanks
                stay blank in documents.
              </p>
            </div>
          </TabsContent>

          <TabsContent value="gst">
            <div className="surface mt-4 p-5" data-testid="settings-gst">
              <div className="grid gap-3 sm:grid-cols-3">
                <Field label="Default GST rate %">
                  <Input
                    data-testid="gst-default-input"
                    value={settings.gst.default_rate}
                    onChange={(e) => set("gst", "default_rate", Number(e.target.value))}
                  />
                </Field>
                <Field label="Place of supply">
                  <Input value={settings.gst.place_of_supply || ""} onChange={(e) => set("gst", "place_of_supply", e.target.value)} />
                </Field>
                <Field label="Pricing mode">
                  <Input value={settings.gst.pricing_mode} onChange={(e) => set("gst", "pricing_mode", e.target.value)} />
                </Field>
              </div>
              <Button className="mt-4 gap-1.5" data-testid="save-gst-button" onClick={() => saveSection("gst")}>
                <Save className="h-4 w-4" /> Save GST settings
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="quotation">
            <div className="surface mt-4 p-5" data-testid="settings-quotation">
              <div className="grid gap-3 sm:grid-cols-3">
                <Field label="Default template">
                  <Input value={settings.quotation.default_template} onChange={(e) => set("quotation", "default_template", e.target.value)} />
                </Field>
                <Field label="Validity (days)">
                  <Input value={settings.quotation.validity_days} onChange={(e) => set("quotation", "validity_days", Number(e.target.value))} />
                </Field>
                <Field label="Minimum margin %">
                  <Input
                    data-testid="min-margin-input"
                    value={settings.quotation.min_margin_percent}
                    onChange={(e) => set("quotation", "min_margin_percent", Number(e.target.value))}
                  />
                </Field>
              </div>
              <Field label="Default terms & conditions" className="mt-3">
                <Textarea
                  rows={5}
                  data-testid="default-terms-input"
                  value={settings.quotation.default_terms || ""}
                  onChange={(e) => set("quotation", "default_terms", e.target.value)}
                />
              </Field>
              <div className="mt-4">
                <div className="overline mb-2">Approval thresholds</div>
                {(settings.quotation.approval_thresholds || []).map((t, i) => (
                  <div key={`${t.role}-${i}`} className="mb-2 flex gap-2">
                    <Input
                      value={t.role}
                      onChange={(e) => {
                        const next = [...settings.quotation.approval_thresholds];
                        next[i] = { ...next[i], role: e.target.value };
                        set("quotation", "approval_thresholds", next);
                      }}
                    />
                    <Input
                      value={t.max_amount}
                      data-testid={`threshold-amount-${i}`}
                      onChange={(e) => {
                        const next = [...settings.quotation.approval_thresholds];
                        next[i] = { ...next[i], max_amount: Number(e.target.value) };
                        set("quotation", "approval_thresholds", next);
                      }}
                    />
                  </div>
                ))}
                <p className="text-xs text-muted-foreground">
                  Quotations up to the role's limit stop at that role. Set 0 for unlimited (owner).
                </p>
              </div>
              <Button className="mt-4 gap-1.5" data-testid="save-quotation-button" onClick={() => saveSection("quotation")}>
                <Save className="h-4 w-4" /> Save quotation settings
              </Button>
              <a href="/admin/records"
                className="mt-4 ml-2 inline-flex h-9 items-center gap-1.5 rounded-md border border-border px-3 text-sm hover:bg-secondary"
                data-testid="open-record-admin-link">
                Record Administration
              </a>
              <a href="/quotations/template-editor"
                className="mt-4 ml-2 inline-flex h-9 items-center gap-1.5 rounded-md border border-border px-3 text-sm hover:bg-secondary"
                data-testid="open-template-editor-link">
                <Save className="h-4 w-4" /> Open Template Editor
              </a>
              <p className="mt-2 text-[11px] text-muted-foreground">
                The Template Editor sets the product image size, shape, spacing and print quality
                on quotation PDFs. Only the Owner or a Director can change it.
              </p>
            </div>
          </TabsContent>

          <TabsContent value="numbering">
            <div className="surface mt-4 p-5" data-testid="settings-numbering">
              <div className="grid gap-3 sm:grid-cols-3">
                {Object.entries(settings.numbering.prefixes || {}).map(([k, v]) => (
                  <Field key={k} label={k.replace(/_/g, " ")}>
                    <Input
                      data-testid={`prefix-${k}-input`}
                      value={v}
                      onChange={(e) =>
                        set("numbering", "prefixes", { ...settings.numbering.prefixes, [k]: e.target.value })
                      }
                    />
                  </Field>
                ))}
              </div>
              <Button className="mt-4 gap-1.5" data-testid="save-numbering-button" onClick={() => saveSection("numbering")}>
                <Save className="h-4 w-4" /> Save numbering
              </Button>
              <p className="mt-2 text-xs text-muted-foreground">
                Documents are numbered PREFIX-FY-0001 with a database counter, so duplicates cannot occur.
              </p>
            </div>
          </TabsContent>

          <TabsContent value="whatsapp">
            <div className="surface mt-4 p-5" data-testid="settings-whatsapp">
              <div className="mb-4 flex items-center gap-2">
                <Badge variant={wa?.configured ? "default" : "destructive"}>
                  {wa?.configured ? "Provider configured" : "Not configured"}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  Outbound documents: {wa?.capabilities?.outbound_documents ? "available" : "unavailable"}
                </span>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Provider">
                  <Input
                    data-testid="wa-provider-input"
                    placeholder="meta_cloud"
                    value={settings.whatsapp.provider || ""}
                    onChange={(e) => set("whatsapp", "provider", e.target.value)}
                  />
                </Field>
                <Field label="Phone number ID">
                  <Input
                    data-testid="wa-phone-id-input"
                    value={settings.whatsapp.phone_number_id || ""}
                    onChange={(e) => set("whatsapp", "phone_number_id", e.target.value)}
                  />
                </Field>
                <Field label="Business account ID">
                  <Input value={settings.whatsapp.business_account_id || ""} onChange={(e) => set("whatsapp", "business_account_id", e.target.value)} />
                </Field>
                <Field label="Webhook verify token">
                  <Input value={settings.whatsapp.webhook_verify_token || ""} onChange={(e) => set("whatsapp", "webhook_verify_token", e.target.value)} />
                </Field>
              </div>
              <Button className="mt-4 gap-1.5" data-testid="save-whatsapp-button" onClick={() => saveSection("whatsapp")}>
                <Save className="h-4 w-4" /> Save WhatsApp settings
              </Button>

              <div className="mt-6 rounded border border-accent/40 bg-accent/5 p-4">
                <div className="mb-2 flex items-center gap-2 text-sm font-medium">
                  <AlertTriangle className="h-4 w-4 text-accent" /> Known provider limitations
                </div>
                <ul className="list-disc space-y-1 pl-5 text-xs text-muted-foreground">
                  {(wa?.limitations || []).map((l) => (
                    <li key={l}>{l}</li>
                  ))}
                </ul>
                <div className="mt-3 text-xs font-medium">Setup steps</div>
                <ol className="list-decimal space-y-1 pl-5 text-xs text-muted-foreground">
                  {(wa?.setup_steps || []).map((s) => (
                    <li key={s}>{s}</li>
                  ))}
                </ol>
              </div>

              <div className="mt-6">
                <div className="overline mb-2">Message templates</div>
                {templates.map((t) => (
                  <div key={t.kind} className="mb-3">
                    <div className="mb-1 text-xs font-medium">{t.kind.replace(/_/g, " ")}</div>
                    <Textarea
                      rows={3}
                      data-testid={`wa-template-${t.kind}`}
                      value={t.body}
                      onChange={(e) => setTemplates((prev) => prev.map((x) => (x.kind === t.kind ? { ...x, body: e.target.value } : x)))}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      className="mt-1"
                      data-testid={`wa-template-save-${t.kind}`}
                      onClick={async () => {
                        try {
                          await api.put("/whatsapp/templates", { kind: t.kind, body: t.body });
                          toast.success("Template saved");
                        } catch (e) {
                          toast.error(apiError(e));
                        }
                      }}
                    >
                      Save template
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="ai">
            <AiSettings settings={settings} set={set} saveSection={saveSection} />
          </TabsContent>

          <TabsContent value="signin">
            <div className="surface mt-4 p-5" data-testid="settings-signin">
              <div className="mb-3 flex items-center gap-2">
                <ShieldCheck className="h-4 w-4" />
                <span className="font-medium">Google sign-in access</span>
              </div>
              <p className="mb-3 text-sm text-muted-foreground">
                Employees can sign in with Google using the email on their employee record.
                Password sign-in keeps working as before.
              </p>
              <label className="flex items-start gap-3 text-sm">
                <Switch
                  data-testid="google-autocreate-switch"
                  checked={Boolean(googlePolicy?.auto_create)}
                  onCheckedChange={(v) => setGooglePolicy({ ...googlePolicy, auto_create: v })}
                />
                <span>
                  Let a new Google account in automatically as a basic Employee
                  <span className="block text-xs text-muted-foreground">
                    Off (recommended): only emails that already exist as employees can sign in.
                  </span>
                </span>
              </label>
              <div className="mt-3 max-w-xs">
                <Field label="Restrict to email domain (optional)">
                  <Input
                    data-testid="google-domain-input"
                    placeholder="pramsia.com"
                    value={googlePolicy?.allowed_domain || ""}
                    onChange={(e) =>
                      setGooglePolicy({ ...googlePolicy, allowed_domain: e.target.value })}
                  />
                </Field>
              </div>
              <Button className="mt-4 gap-1.5" data-testid="save-signin-button" onClick={saveGooglePolicy}>
                <Save className="h-4 w-4" /> Save sign-in rules
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="demo">            <div className="surface mt-4 p-5" data-testid="settings-demo">
              <div className="mb-3 flex items-center gap-2">
                <Database className="h-4 w-4" />
                <span className="text-sm font-medium">Demo data</span>
                {demo?.seeded && <Badge className="bg-accent text-accent-foreground">DEMO DATA PRESENT</Badge>}
              </div>
              <p className="mb-4 text-xs text-muted-foreground">
                Creates realistic customers, products with photographs, quotations, stock, projects,
                expenses and AI employee requests. Every record is tagged so it can be purged before
                real company data goes in.
              </p>
              {demo && (
                <div className="mb-4 grid grid-cols-2 gap-2 text-xs sm:grid-cols-5">
                  {Object.entries(demo.counts).map(([k, v]) => (
                    <div key={k} className="rounded border border-border p-2">
                      <div className="overline">{k}</div>
                      <div className="font-mono-num">{v}</div>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <Button
                  data-testid="seed-demo-button"
                  disabled={busy === "seed"}
                  onClick={async () => {
                    setBusy("seed");
                    try {
                      const { data } = await api.post("/demo/seed");
                      toast.success(data.message);
                      load();
                    } catch (e) {
                      toast.error(apiError(e));
                    } finally {
                      setBusy("");
                    }
                  }}
                >
                  {busy === "seed" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Seed demo data
                </Button>
                <Button
                  variant="destructive"
                  data-testid="purge-demo-button"
                  className="gap-1.5"
                  onClick={async () => {
                    if (!window.confirm("Remove all records tagged as demo data?")) return;
                    try {
                      await api.delete("/demo/purge");
                      toast.success("Demo data removed");
                      load();
                    } catch (e) {
                      toast.error(apiError(e));
                    }
                  }}
                >
                  <Trash2 className="h-4 w-4" /> Purge demo data
                </Button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="audit">
            <AuditLog />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
